import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ═══════════════════════════════════════════════════════════════════════════════
// TWP GLASS ORDER RANGE PDF — LANDSCAPE
// Manufacturer: Tracy Corners IG
// ═══════════════════════════════════════════════════════════════════════════════

const BRAND_BROWN = [139,  69,  19];
const BRAND_LIGHT = [200, 160, 110];
const WHITE       = [255, 255, 255];
const LIGHT_GRAY  = [240, 240, 240];
const MID_GRAY    = [180, 180, 180];
const DARK        = [ 40,  40,  40];
const LABEL_COLOR = [100,  60,  20];
const BLUE_BG     = [232, 244, 255];
const BLUE_TEXT   = [ 20,  60, 120];
const MFG_GREEN   = [ 20,  90,  45];
const MFG_BG      = [220, 245, 228];
const RED_TEXT    = [180,  30,  30];
const RED_BG      = [255, 240, 240];

const R_BG   = [255, 237, 213];
const R_TEXT = [154,  52,   0];
const R_RULE = [251, 191, 136];

const PAGE_W  = 279;
const PAGE_H  = 216;
const MARGIN  = 14;
const HDR_H   = 38;
const FTR_H   = 18;
const STRIP_H = 10;

const ROW_H        = 14;
const COL_HDR_H    = 12;
const JOB_BANNER_H = 9;
const NOTICE_ROW_H = 6;
const NOTICE_PAD_H = 10;

const STANDARD_OA = '7/8"';

const TLA_CONFIGS  = ['TLA', 'TDLA', 'TLK', 'TDC', 'TDCA'];
const SG_CONFIGS   = ['SG'];
const SPECIAL_OPTS = ['obscure', 'gray tint', 'bronze tint', 'tempered'];

const CONFIG_LABELS = {
  TLA:  'Triple Low-E Argon',
  TDLA: 'Triple Double Low-E Argon',
  TLK:  'Triple Low-E Krypton',
  TDC:  'Triple Double Clear',
  TDCA: 'Triple Double Clear Argon',
  DLA:  'Double Low-E Argon',
  DCA:  'Double Clear Argon',
  DC:   'Double Clear',
  SG:   'Single Pane Glass',
};

// ── Grid type label ───────────────────────────────────────────────────────────
function gridTypeLabel(pane) {
  if (!pane.grids) return '';
  const hasQty = parseInt(pane.gridQtyV) > 0 || parseInt(pane.gridQtyH) > 0;
  if (!hasQty) return '';
  const t = (pane.gridType || '').toLowerCase().trim();
  if (t === 'flat')      return 'Flat';
  if (t === 'contoured') return 'Contoured';
  if (t)                 return pane.gridType;
  return 'Yes';
}

function isSpecialOption(opt) {
  return SPECIAL_OPTS.some(o => (opt || '').toLowerCase().includes(o));
}
function isNonStandardOA(oaThick) {
  return oaThick && oaThick.trim() !== STANDARD_OA;
}
function isSpecialRow(row) {
  const pkg = (row.glassType || '').toUpperCase().trim();
  return TLA_CONFIGS.includes(pkg) || SG_CONFIGS.includes(pkg)
    || !!row.grids
    || isSpecialOption(row.glassConfig)
    || isNonStandardOA(row.oaThick);
}
function specialRowReason(row) {
  const pkg = (row.glassType || '').toUpperCase().trim();
  const r   = [];
  if (TLA_CONFIGS.includes(pkg) || SG_CONFIGS.includes(pkg))
    r.push(`${pkg} — ${CONFIG_LABELS[pkg] || pkg}`);
  if (row.grids) {
    const gc = (row.gridColor || '').trim();
    const gcNote = (gc && gc.toLowerCase() !== 'white') ? ` (${gc})` : '';
    r.push(`Grids: ${row.grids}${gcNote}`);
  }
  if (isSpecialOption(row.glassConfig)) r.push(`Option: ${row.glassConfig}`);
  if (isNonStandardOA(row.oaThick))   r.push(`OA Thick: ${row.oaThick}`);
  return r.join(' + ');
}

// ── Page height helpers ───────────────────────────────────────────────────────
function estimateGroupHeight(tableRows) {
  const sc      = tableRows.filter(isSpecialRow).length;
  const noticeH = sc > 0 ? NOTICE_PAD_H + sc * NOTICE_ROW_H + 2 : 0;
  return noticeH + JOB_BANNER_H + COL_HDR_H + tableRows.length * ROW_H + 6;
}
function maxSafeHeight(isFirstPage) {
  return PAGE_H - FTR_H - (isFirstPage ? HDR_H : STRIP_H) - 6;
}

// ── Attention notice ──────────────────────────────────────────────────────────
function drawAttentionNotice(doc, lineInfos, noticeY, isRepair) {
  if (!lineInfos.length) return noticeY;
  const fillCol   = isRepair ? [255, 237, 213] : MFG_BG;
  const borderCol = isRepair ? [200, 100,  40] : MFG_GREEN;
  const textCol   = isRepair ? [154,  52,   0] : MFG_GREEN;
  const padV = 3, rowH = NOTICE_ROW_H;
  const noticeX = MARGIN, noticeW = PAGE_W - MARGIN * 2;
  const noticeH = padV * 2 + lineInfos.length * rowH;
  doc.setFillColor(...fillCol); doc.setDrawColor(...borderCol); doc.setLineWidth(0.4);
  doc.roundedRect(noticeX, noticeY, noticeW, noticeH, 1.5, 1.5, 'FD');
  doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...textCol);
  const attnTxt = 'ATTENTION:';
  const attnW   = doc.getTextWidth(attnTxt);
  doc.text(attnTxt, noticeX + 5, noticeY + padV + 3.5);
  const entryX = noticeX + 5 + attnW + 3;
  lineInfos.forEach((info, i) => {
    const lineY = noticeY + padV + i * rowH + 3.5;
    doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.setTextColor(...textCol);
    const tag = `Line ${info.lineNum}`;
    doc.text(tag, entryX, lineY);
    const tagW = doc.getTextWidth(tag);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...DARK);
    const lbl = `  (${info.label})`;
    doc.text(lbl, entryX + tagW, lineY);
    const lblW = doc.getTextWidth(lbl);
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...textCol);
    doc.text(`  —  ${info.reason}`, entryX + tagW + lblW, lineY);
    if (i < lineInfos.length - 1) {
      doc.setDrawColor(...borderCol); doc.setLineWidth(0.15);
      doc.line(noticeX + 3, noticeY + padV + (i + 1) * rowH,
               noticeX + noticeW - 3, noticeY + padV + (i + 1) * rowH);
    }
  });
  doc.setLineWidth(0.3); doc.setTextColor(...DARK); doc.setDrawColor(...LIGHT_GRAY);
  return noticeY + noticeH + 2;
}

// ── buildLineInfos — CORRECT line numbers ─────────────────────────────────────
// Walks the full tableRows array so lineNum = actual 1-based position.
// Never filters first — that was the bug that always showed "Line 1".
function buildLineInfos(tableRows) {
  const infos = [];
  tableRows.forEach((row, idx) => {
    if (isSpecialRow(row)) {
      infos.push({ lineNum: idx + 1, label: row.label, reason: specialRowReason(row) });
    }
  });
  return infos;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function toFraction(val) {
  if (val == null || val === '') return '—';
  const raw = parseFloat(val);
  if (isNaN(raw)) return String(val);
  const n       = Math.round(raw * 16) / 16;
  const whole   = Math.floor(n);
  const decimal = n - whole;
  if (Math.abs(decimal) < 0.001) return String(whole);
  const cf = [
    [0.0625,'1/16'],[0.125,'1/8'],[0.1875,'3/16'],[0.25,'1/4'],[0.3125,'5/16'],
    [0.375,'3/8'],[0.4375,'7/16'],[0.5,'1/2'],[0.5625,'9/16'],[0.625,'5/8'],
    [0.6875,'11/16'],[0.75,'3/4'],[0.8125,'13/16'],[0.875,'7/8'],[0.9375,'15/16'],
  ];
  for (const [d, f] of cf) {
    if (Math.abs(decimal - d) < 0.001) return whole === 0 ? f : `${whole} ${f}`;
  }
  return n.toFixed(4);
}
function dec(val)  { return toFraction(val); }
function evenUp(n) { return Math.ceil(n / 2) * 2; }

function calcSqFt(w, h) {
  const wn = parseFloat(w), hn = parseFloat(h);
  if (isNaN(wn) || isNaN(hn) || wn <= 0 || hn <= 0) return '';
  const raw = (wn * hn) / 144;
  if (raw < 0.01) return '';
  return String(Math.max(3, parseFloat(((evenUp(wn) * evenUp(hn)) / 144).toFixed(2))));
}
function calcGlassTypeDsTs(w, h) {
  const wn = parseFloat(w), hn = parseFloat(h);
  if (isNaN(wn) || isNaN(hn)) return '—';
  return (wn * hn / 144) <= 20 ? 'DS' : 'TS';
}
function deriveGasFill(pkg) {
  if (!pkg) return '—';
  const c = pkg.toUpperCase().trim();
  if (['DCA','DLA','TLA','TDCA','TDLA'].includes(c)) return 'Argon';
  if (c === 'TLK')              return 'Krypton';
  if (['DC','TDC'].includes(c)) return 'No Gas';
  if (c === 'SG')               return '—';
  return 'Argon';
}
function paneSuffix(pl) {
  if (!pl) return '';
  const l = pl.toLowerCase();
  if (l.includes('center'))    return 'CC';
  if (l.includes('keeper'))    return 'K';
  if (l.includes('lock'))      return 'L';
  if (l.includes('casement'))  return 'C';
  if (l.includes('slider'))    return 'S';
  if (l.includes('picture'))   return 'P';
  if (l.includes('awning'))    return 'A';
  if (l.includes('basement'))  return 'B';
  if (l.includes('transom'))   return 'T';
  if (l.includes('sidelight')) return 'SL';
  if (l.includes('fixed'))     return 'F';
  if (l.includes('upper'))     return 'U';
  if (l.includes('lower'))     return 'LW';
  if (l.includes('bay'))       return 'BY';
  return pl.slice(0, 2).toUpperCase();
}

// ── Logo ──────────────────────────────────────────────────────────────────────
const LOGO_URL = '/brand/twp-logo.png';
let _logo = null;
async function getLogo() {
  if (_logo) return _logo;
  try {
    const res  = await fetch(LOGO_URL);
    const blob = await res.blob();
    return new Promise(r => {
      const fr = new FileReader();
      fr.onloadend = () => { _logo = fr.result; r(_logo); };
      fr.onerror   = () => r(null);
      fr.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ── Page headers / strip / footer ─────────────────────────────────────────────
function drawPage1Header(doc, from, to, logo, pg, total) {
  doc.setFillColor(...WHITE); doc.rect(0, 0, PAGE_W, HDR_H, 'F');
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(3);  doc.line(0, 0, 0, HDR_H);
  doc.setLineWidth(0.8); doc.line(0, HDR_H, PAGE_W, HDR_H);
  doc.setLineWidth(0.3);
  if (logo) { try { doc.addImage(logo, 'PNG', MARGIN, 4, 18, 18); } catch {} }
  doc.setTextColor(...BRAND_BROWN); doc.setFontSize(13); doc.setFont('helvetica', 'bold');
  doc.text('The Window Place LLC', MARGIN + 22, 12);
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('1300 E 4th Ave  •  Milbank, SD 57252  •  (605) 432-1834  •  thewindowplacellc.com', MARGIN + 22, 17.5);
  doc.setFontSize(18); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BLUE_TEXT);
  doc.text('GLASS ORDER', PAGE_W - MARGIN, 14, { align: 'right' });
  doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.3); doc.line(MARGIN, 22, PAGE_W - MARGIN, 22);
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('Date Range:', MARGIN, 28);
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
  doc.text(`${from}  —  ${to}`, MARGIN + 22, 28);
  const mfgTxt = 'Mfg: Tracy Corners IG';
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
  const mw = doc.getTextWidth(mfgTxt) + 9;
  const mx = PAGE_W / 2, my = 23.5;
  doc.setFillColor(...MFG_BG); doc.roundedRect(mx - mw / 2, my, mw, 7, 1.5, 1.5, 'F');
  doc.setDrawColor(...MFG_GREEN); doc.setLineWidth(0.35); doc.roundedRect(mx - mw / 2, my, mw, 7, 1.5, 1.5, 'S');
  doc.setTextColor(...MFG_GREEN); doc.text(mfgTxt, mx, my + 5, { align: 'center' });
  doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text('Page:', PAGE_W - MARGIN - 30, 28);
  doc.setFont('helvetica', 'bold'); doc.setTextColor(...DARK);
  doc.text(`${pg} of ${total}`, PAGE_W - MARGIN - 18, 28);
  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...MID_GRAY);
  const ts = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + '  ' + new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  doc.text(`Printed: ${ts}`, MARGIN, 34);
}

function drawStrip(doc, from, to, pg, total) {
  doc.setFillColor(...WHITE); doc.rect(0, 0, PAGE_W, STRIP_H, 'F');
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(3);  doc.line(0, 0, 0, STRIP_H);
  doc.setLineWidth(0.5); doc.line(0, STRIP_H, PAGE_W, STRIP_H);
  doc.setLineWidth(0.3);
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BLUE_TEXT);
  doc.text('GLASS ORDER', MARGIN + 2, STRIP_H - 2.5);
  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
  doc.text(`${from}  —  ${to}`, MARGIN + 36, STRIP_H - 2.5);
  doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...MFG_GREEN);
  doc.text('Mfg: Tracy Corners IG', PAGE_W / 2, STRIP_H - 2.5, { align: 'center' });
  doc.setFontSize(7); doc.setTextColor(...DARK);
  doc.text(`${pg} of ${total}`, PAGE_W - MARGIN, STRIP_H - 2.5, { align: 'right' });
}

function drawFooter(doc) {
  const y = PAGE_H - FTR_H;
  doc.setFillColor(...WHITE); doc.rect(0, y, PAGE_W, FTR_H, 'F');
  doc.setDrawColor(...BRAND_LIGHT);
  doc.setLineWidth(3);  doc.line(0, y, 0, PAGE_H);
  doc.setLineWidth(0.8); doc.line(0, y, PAGE_W, y);
  doc.setLineWidth(0.3);
  doc.setTextColor(...LABEL_COLOR); doc.setFontSize(7); doc.setFont('helvetica', 'normal');
  doc.text(
    'The Window Place LLC  •  1300 E 4th Ave, Milbank, SD 57252  •  (605) 432-1834  •  office@thewindowplacellc.com',
    PAGE_W / 2, y + 4.5, { align: 'center' }
  );
  // ── Grid color spec note — helps Tracy Corners understand the column ──
  doc.setTextColor(60, 90, 60); doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
  doc.text(
    'Grid spec: outside face is always WHITE. Inside face matches the INSIDE GRID COLOR shown per line.',
    PAGE_W / 2, y + 9, { align: 'center' }
  );
  doc.setFont('helvetica', 'normal');
}

// ── Column definitions ────────────────────────────────────────────────────────
// Total = 241 mm  (content area = 251 mm — 10 mm of breathing room)
//
// Key widths:
//   GAS FILL   17 mm — fits "Krypton" (longest gas fill value)
//   GRID TYPE  19 mm — fits "Contoured" (longest grid type value)
//   GRID PATTERN 20 mm — fits "Colonial"
//   GRID COLOR   20 mm — fits "dk woodgrain"
//   VERT / HORI   8 mm — numbers only
const TABLE_COLS = [
  { header: 'QTY',           dataKey: 'qty',         width: 8  },
  { header: 'WIDTH',         dataKey: 'width',        width: 22 },
  { header: 'HEIGHT',        dataKey: 'height',       width: 22 },
  { header: 'LABEL',         dataKey: 'label',        width: 16 },
  { header: 'GLASS\nTYPE',  dataKey: 'dsTs',         width: 11 },
  { header: 'GLASS\nCONFIG',dataKey: 'glassType',    width: 13 },
  { header: 'GAS FILL',      dataKey: 'gasFill',      width: 17 }, // fits Krypton
  { header: 'OPTIONS',       dataKey: 'glassConfig',  width: 17 },
  { header: 'GRID\nTYPE',   dataKey: 'grids',        width: 19 }, // fits Contoured
  { header: 'GRID\nPATTERN',dataKey: 'gridPattern',  width: 20 }, // fits Colonial
  { header: 'VERT',          dataKey: 'gridV',        width: 8  },
  { header: 'HORI',          dataKey: 'gridH',        width: 8  },
  { header: 'INSIDE GRID\nCOLOR',  dataKey: 'gridColor',    width: 28 }, // fits 'Dk Woodgrain' at font 8
  { header: 'OA\nTHICK',    dataKey: 'oaThick',      width: 12 },
  { header: 'AIR\nSPACE',   dataKey: 'airSpace',     width: 11 },
  { header: 'SQ FT',         dataKey: 'sqft',         width: 11 },
]; // total = 235 mm ✓

const OPTIONS_COL      = 7;
const GRIDS_COL        = 8;
const GRID_PATTERN_COL = 9;
const OA_THICK_COL     = 13;
const GLASS_CFG_COL    = 5;

const COL_STYLES = {};
TABLE_COLS.forEach((c, i) => { COL_STYLES[i] = { cellWidth: c.width, halign: 'center' }; });
COL_STYLES[3]  = { cellWidth: TABLE_COLS[3].width,  halign: 'center', fontStyle: 'bold', textColor: LABEL_COLOR };
COL_STYLES[4]  = { cellWidth: TABLE_COLS[4].width,  halign: 'center', fontStyle: 'bold', textColor: BLUE_TEXT };
COL_STYLES[5]  = { cellWidth: TABLE_COLS[5].width,  halign: 'center', fontStyle: 'bold', textColor: BLUE_TEXT };
COL_STYLES[8]  = { cellWidth: TABLE_COLS[8].width,  halign: 'center', fontStyle: 'bold' };
COL_STYLES[15] = { cellWidth: TABLE_COLS[15].width, halign: 'right' };
// Larger font for dimensions
COL_STYLES[1]  = { cellWidth: TABLE_COLS[1].width,  halign: 'center', fontStyle: 'bold', fontSize: 9.5 };
COL_STYLES[2]  = { cellWidth: TABLE_COLS[2].width,  halign: 'center', fontStyle: 'bold', fontSize: 9.5 };
// VERT/HORI — bold but smaller since they're just numbers
COL_STYLES[10] = { cellWidth: TABLE_COLS[10].width, halign: 'center', fontStyle: 'bold', fontSize: 9 };
COL_STYLES[11] = { cellWidth: TABLE_COLS[11].width, halign: 'center', fontStyle: 'bold', fontSize: 9 };

function makeRepairColStyles() {
  const s = {};
  TABLE_COLS.forEach((c, i) => { s[i] = { cellWidth: c.width, halign: 'center' }; });
  s[3]  = { cellWidth: TABLE_COLS[3].width,  halign: 'center', fontStyle: 'bold', textColor: R_TEXT };
  s[4]  = { cellWidth: TABLE_COLS[4].width,  halign: 'center', fontStyle: 'bold', textColor: R_TEXT };
  s[5]  = { cellWidth: TABLE_COLS[5].width,  halign: 'center', fontStyle: 'bold', textColor: R_TEXT };
  s[8]  = { cellWidth: TABLE_COLS[8].width,  halign: 'center', fontStyle: 'bold' };
  s[15] = { cellWidth: TABLE_COLS[15].width, halign: 'right' };
  s[1]  = { cellWidth: TABLE_COLS[1].width,  halign: 'center', fontStyle: 'bold', fontSize: 9.5 };
  s[2]  = { cellWidth: TABLE_COLS[2].width,  halign: 'center', fontStyle: 'bold', fontSize: 9.5 };
  s[10] = { cellWidth: TABLE_COLS[10].width, halign: 'center', fontStyle: 'bold', fontSize: 9 };
  s[11] = { cellWidth: TABLE_COLS[11].width, halign: 'center', fontStyle: 'bold', fontSize: 9 };
  return s;
}

// ── Cell hooks ────────────────────────────────────────────────────────────────
function makeDidParseCell(tableRows) {
  return function (data) {
    if (data.section !== 'body') return;
    const row = tableRows[data.row.index];
    if (!row) return;

    // Bold entire special rows
    if (isSpecialRow(row)) {
      data.cell.styles.fontStyle = 'bold';
      data.cell.styles.textColor = [0, 0, 0];
    }

    // OPTIONS — blank if None/empty; green if special option
    if (data.column.index === OPTIONS_COL) {
      const opt = row.glassConfig;
      if (!opt || opt === 'None' || opt === '') { data.cell.text = ['']; }
      else if (isSpecialOption(opt)) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.textColor = MFG_GREEN;
        data.cell.styles.fillColor = MFG_BG;
      }
    }

    // GRID TYPE — 'Flat' green, 'Contoured' blue, empty = blank
    if (data.column.index === GRIDS_COL) {
      const gl = row.grids;
      if (!gl) { data.cell.text = ['']; return; }
      if (gl.toLowerCase() === 'flat') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.textColor = MFG_GREEN;
      } else if (gl.toLowerCase() === 'contoured') {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.textColor = BLUE_TEXT;
      }
    }

    // GRID PATTERN — green fill when grids present
    if (data.column.index === GRID_PATTERN_COL && row.grids) {
      data.cell.styles.fontStyle = 'bold';
      data.cell.styles.textColor = MFG_GREEN;
      data.cell.styles.fillColor = MFG_BG;
    }

    // GLASS CONFIG — SG = red
    if (data.column.index === GLASS_CFG_COL &&
        SG_CONFIGS.includes((row.glassType || '').toUpperCase().trim())) {
      data.cell.styles.fontStyle = 'bold';
      data.cell.styles.textColor = RED_TEXT;
      data.cell.styles.fillColor = RED_BG;
    }

    // OA THICK — non-standard = red
    if (data.column.index === OA_THICK_COL && isNonStandardOA(row.oaThick)) {
      data.cell.styles.fontStyle = 'bold';
      data.cell.styles.textColor = RED_TEXT;
      data.cell.styles.fillColor = RED_BG;
    }
  };
}

function makeWillDrawCell(doc, tableRows) {
  return function (data) {
    if (data.section !== 'body') return;
    const row = tableRows[data.row.index];
    if (!row || !isSpecialRow(row)) return;
    const { x, y, width: cw, height: ch } = data.cell;
    doc.setDrawColor(0, 0, 0); doc.setLineWidth(1.0);
    doc.line(x, y, x + cw, y); doc.line(x, y + ch, x + cw, y + ch);
    if (data.column.index === 0) doc.line(x, y, x, y + ch);
    if (data.column.index === TABLE_COLS.length - 1) doc.line(x + cw, y, x + cw, y + ch);
    doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.2);
  };
}

// ── Render autoTable for one group ───────────────────────────────────────────
function renderTable(doc, tableRows, startY, colStyles, headStylesOverride, onNewPage) {
  // Track whether we've already seen the first table page so we only call
  // onNewPage for CONTINUATION pages, not the first one (which already has
  // chrome drawn by the caller before renderTable runs).
  let seenFirstPage = false;
  autoTable(doc, {
    startY,
    head:  [TABLE_COLS.map(c => c.header)],
    body:  tableRows.map(r => TABLE_COLS.map(c => r[c.dataKey] ?? '')),
    styles: {
      fontSize:    8,
      cellPadding: { top: 1.5, bottom: 1.5, left: 2, right: 2 },
      textColor:   DARK, lineColor: LIGHT_GRAY, lineWidth: 0.2,
      overflow: 'hidden', halign: 'center',
    },
    headStyles: {
      fillColor: BLUE_BG, textColor: BLUE_TEXT, fontStyle: 'bold',
      fontSize: 5.5, halign: 'center', valign: 'middle', minCellHeight: 10,
      ...(headStylesOverride || {}),
    },
    bodyStyles:         { minCellHeight: ROW_H },
    alternateRowStyles: { fillColor: [246, 251, 255] },
    columnStyles:       colStyles || COL_STYLES,
    // top margin = STRIP_H + 4 reserves space at the top of every continuation
    // page so autoTable does NOT draw over the strip chrome.
    margin:             { left: MARGIN, right: MARGIN, top: STRIP_H + 4, bottom: FTR_H + 6 },
    tableLineColor:     LIGHT_GRAY,
    tableLineWidth:     0.2,
    // `willAddPage` is NOT a real jspdf-autotable hook in current versions —
    // it was silently ignored, which is why continuation pages had no chrome
    // and pg never incremented. Use didDrawPage with a first-page skip.
    didDrawPage:        (data) => {
      if (seenFirstPage && onNewPage) onNewPage();
      seenFirstPage = true;
    },
    didParseCell:       makeDidParseCell(tableRows),
    willDrawCell:       makeWillDrawCell(doc, tableRows),
  });
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ═══════════════════════════════════════════════════════════════════════════════
export async function generateGlassOrderRangePDF(jobs, dateFrom, dateTo, returnBlob = false, repairItems = [], returnData = false) {

  // ── Build job groups ──────────────────────────────────────────────────────
  const groups = [];
  // ── Rollup helpers (must match single Glass Order PDF logic exactly) ─────
  const norm = v => (v === null || v === undefined) ? '' : String(v).trim().toLowerCase();
  const nf = v => { const n = parseFloat(v); return isNaN(n) ? '' : String(n); };
  const winKey = w => [
    norm(w.type || w.code || w.series),
    nf(w.overallW || w.width),
    nf(w.height),
    norm(w.color || w.frameColor),
    norm(w.handing),
    JSON.stringify((w.glass || []).map(p => [nf(p.width), nf(p.height), norm(p.glassType), norm(p.glassConfig), norm(p.gridPattern), nf(p.gridQtyV), nf(p.gridQtyH), norm(p.gridColor)])),
  ].join('|');
  const isPW = w => norm(w.type || w.code || w.series).startsWith('pw') || norm(w.series).includes('picture');

  jobs.forEach(job => {
    const tableRows = [];

    // NO MERGE, ENTRY ORDER — every window emits its own glass panes, in the
    // order entered. Rolling windows by spec previously collapsed two separate
    // windows into one group and dropped the absorbed window's panes entirely
    // (DB counted them, PDF did not — the 6-pane shortfall). Same rule as the
    // single glass order, flexscreen, and shop order. PW-first sort removed too.
    const rolledWindows = (job.calculatedWindows || []);

    rolledWindows.forEach(win => {
      (win.glass || []).forEach(pane => {
        const gridLabel = gridTypeLabel(pane);
        const hasGrids  = !!gridLabel;
        const baseLabel = win.label || `${job.woNumber || ''}`;
        const suffix    = paneSuffix(pane.label || '');
        const mfgLabel  = suffix ? `${baseLabel}${suffix}` : baseLabel;
        const glassPkg  = (pane.glassType   || 'DLA').trim();
        const glassOpt  = (pane.glassConfig || '').trim();
        tableRows.push({
          // QTY FIX — pane.qty already includes window count; no re-multiply.
          qty:         String(parseInt(pane.qty) || 1),
          width:       dec(pane.width),
          height:      dec(pane.height),
          label:       mfgLabel,
          dsTs:        calcGlassTypeDsTs(pane.width, pane.height),
          glassType:   glassPkg,
          gasFill:     deriveGasFill(glassPkg),
          glassConfig: glassOpt,
          grids:       gridLabel,
          gridPattern: hasGrids ? (pane.gridPattern || '') : '',
          gridV:       hasGrids ? String(pane.gridQtyV || 0) : '',
          gridH:       hasGrids ? String(pane.gridQtyH || 0) : '',
          gridColor:   hasGrids ? (pane.gridColor || '') : '',
          oaThick:     '7/8"',
          airSpace:    '5/8"',
          sqft:        calcSqFt(pane.width, pane.height),
          _qty:        parseInt(pane.qty) || 1,
        });
      });
    });
    if (tableRows.length) groups.push({ job, tableRows });
  });

  // ── Build repair rows grouped by customer name + WO# ──────────────────────
  // Each repair row now carries customerName + woNumber from the dashboard.
  // Group key = customerName||'Walk-in' so two different WOs under the same
  // customer name stay together; WO# gets shown in the banner meta line.
  const validRepairItems = (repairItems || []).filter(r => r.width && r.height);
  const repairCustomerMap = new Map();
  validRepairItems.forEach(r => {
    const customerName = (r.customerName || '').trim() || 'Walk-in / Unknown';
    const woNumber     = (r.woNumber || '').trim();
    // Key on customer + WO so "Noah 8899" and "Noah 8900" render as separate banners
    const key = `${customerName}||${woNumber}`;
    if (!repairCustomerMap.has(key)) {
      repairCustomerMap.set(key, { customerName, woNumber, tableRows: [] });
    }
    const gridLabel = gridTypeLabel(r);
    const hasGrids  = !!gridLabel;
    const glassPkg  = (r.glassType   || 'DLA').trim();
    const glassOpt  = (r.glassConfig || '').trim();
    repairCustomerMap.get(key).tableRows.push({
      qty:         String(r.qty || 1),
      width:       dec(r.width),
      height:      dec(r.height),
      label:       r.label || 'Repair',
      dsTs:        calcGlassTypeDsTs(r.width, r.height),
      glassType:   glassPkg,
      gasFill:     deriveGasFill(glassPkg),
      glassConfig: glassOpt,
      grids:       gridLabel,
      gridPattern: hasGrids ? (r.gridPattern || '') : '',
      gridV:       hasGrids ? String(r.gridQtyV || 0) : '',
      gridH:       hasGrids ? String(r.gridQtyH || 0) : '',
      gridColor:   hasGrids ? (r.gridColor || '') : '',
      oaThick:     r.oaThick || '7/8"',
      airSpace:    '5/8"',
      sqft:        calcSqFt(r.width, r.height),
      _qty:        parseInt(r.qty || 1) || 1,
      // DEFECTIVE ON ARRIVAL — carried through so the PDF can flag supplier remakes
      defect:      (r.defect && r.defect !== 'None') ? r.defect : '',
      defectOther: r.defectOther || '',
    });
  });

  const repairGroups = Array.from(repairCustomerMap.values());

  if (!groups.length && !repairGroups.length) {
    alert('No glass panes found in the selected date range.');
    return;
  }

  const logo = await getLogo();

  // ── Page count simulation ─────────────────────────────────────────────────
  function countPages() {
    // Simulate same bin-packing algo as render
    const queue = [
      ...groups.map(g => estimateGroupHeight(g.tableRows)),
      ...repairGroups.map(g => estimateGroupHeight(g.tableRows)),
    ];
    const placed = new Array(queue.length).fill(false);
    let pages = 1;
    let usedY = HDR_H + 4;
    const fullSafe = maxSafeHeight(false);

    // Hard ceiling: the loop can place at most queue.length items, one per pass
    // in the worst case, plus one page-break pass each. 2x + 5 is comfortable
    // headroom and makes an infinite loop impossible regardless of geometry.
    let guard = queue.length * 2 + 5;
    while (placed.some(p => !p) && guard-- > 0) {
      let placedThisPass = false;
      for (let i = 0; i < queue.length; i++) {
        if (placed[i]) continue;
        const h = queue[i];
        const remaining = (PAGE_H - FTR_H - 6) - usedY;
        const topOfPage = (pages === 1 ? usedY <= HDR_H + 5 : usedY <= STRIP_H + 5);
        const tooBig = h > fullSafe;
        if (h <= remaining || (tooBig && topOfPage)) {
          usedY += h;
          // If single group taller than one page, autoTable adds pages
          if (tooBig) pages += Math.ceil((h - fullSafe) / fullSafe);
          placed[i] = true;
          placedThisPass = true;
        }
      }
      if (!placedThisPass && placed.some(p => !p)) {
        pages++;
        usedY = STRIP_H + 4;
      }
    }
    // If the guard tripped, some items never satisfied the fit test (boundary
    // geometry). Account for each remaining item as its own page so the page
    // count is not short — render uses doc.getNumberOfPages() as final truth.
    if (placed.some(p => !p)) {
      const leftover = placed.filter(p => !p).length;
      pages += leftover;
    }
    return Math.max(1, pages);
  }

  const totalPages = countPages();
  const doc        = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });

  let pg   = 1;
  let curY = HDR_H + 4;

  drawPage1Header(doc, dateFrom, dateTo, logo, 1, totalPages);
  drawFooter(doc);

  function newPage() {
    doc.addPage(); pg++;
    drawStrip(doc, dateFrom, dateTo, pg, totalPages);
    drawFooter(doc);
    curY = STRIP_H + 4;
  }

  function ensureSpace(neededH) {
    const available = (PAGE_H - FTR_H - 6) - curY;
    if (neededH <= available) return; // fits on current page — stay
    // Group doesn't fit on current page. Push to a fresh page so the customer
    // banner is NEVER orphaned from its data rows. A fresh page always gives
    // more breathing room (unless we're already at the top of one).
    // If the group is taller than a single page, autoTable will paginate it
    // properly on the new page — the banner will at least be with the first
    // chunk of its data rows instead of stranded at the bottom of a prior page.
    const freshTop = STRIP_H + 4;
    if (curY > freshTop + 1) newPage();
  }

  // ── Render groups (bin-packing first-fit preserving order) ──────────────
  // Build a unified queue of jobs + repairs. Walk page-by-page, placing every
  // unplaced group that fits in remaining space. When nothing more fits, new
  // page. This means page 1 is NEVER blank — if the first job is too tall,
  // smaller jobs from later in the queue slot in first. Display badges renumber
  // by placement order, not original index.
  let grandPanes = 0, grandQty = 0;
  let jobDisplayCounter    = 0;
  let repairDisplayCounter = 0;
  const repairColStyles = repairGroups.length > 0 ? makeRepairColStyles() : null;

  // Unified queue: keep job order then repairs (matches original ordering)
  const renderQueue = [
    ...groups.map((g, i) => ({ kind: 'job', data: g, origIdx: i, height: estimateGroupHeight(g.tableRows) })),
    ...repairGroups.map((g, i) => ({ kind: 'repair', data: g, origIdx: i, height: estimateGroupHeight(g.tableRows) })),
  ];
  const placed = new Array(renderQueue.length).fill(false);

  function renderJobGroup({ job, tableRows }) {
    const jobQty    = tableRows.reduce((s, r) => s + r._qty, 0);
    const gridCount = tableRows.filter(r => !!r.grids).length;

    const lineInfos = buildLineInfos(tableRows);
    if (lineInfos.length > 0) curY = drawAttentionNotice(doc, lineInfos, curY, false);

    const BH = JOB_BANNER_H;
    doc.setFillColor(...BLUE_BG);
    doc.rect(MARGIN, curY, PAGE_W - MARGIN * 2, BH, 'F');
    jobDisplayCounter++;
    const numStr = String(jobDisplayCounter);
    doc.setFontSize(7); doc.setFont('helvetica', 'bold');
    const numW = doc.getTextWidth(numStr) + 5;
    doc.setFillColor(...BLUE_TEXT);
    doc.roundedRect(MARGIN, curY, numW + 2, BH, 1, 1, 'F');
    doc.setTextColor(...WHITE);
    doc.text(numStr, MARGIN + (numW + 2) / 2, curY + BH - 2.2, { align: 'center' });
    doc.setDrawColor(...LIGHT_GRAY); doc.setLineWidth(0.3);
    doc.line(MARGIN, curY + BH, PAGE_W - MARGIN, curY + BH);
    const nameX = MARGIN + numW + 5;
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...BLUE_TEXT);
    doc.text(job.customerName || '—', nameX, curY + BH - 2.2);
    doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
    let meta = `WO# ${job.woNumber || '—'}   •   ${tableRows.length} pane${tableRows.length !== 1 ? 's' : ''}   •   Qty: ${jobQty}`;
    if (gridCount > 0) meta += `   •   ${gridCount} with grids`;
    doc.text(meta, nameX + 72, curY + BH - 2.2);
    if (job.orderDate) {
      const od = new Date(job.orderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      doc.setFontSize(6); doc.setTextColor(...MID_GRAY);
      doc.text(od, PAGE_W - MARGIN - 2, curY + BH - 2.2, { align: 'right' });
    }
    curY += BH;

    renderTable(doc, tableRows, curY, COL_STYLES, null, () => {
      pg++; drawStrip(doc, dateFrom, dateTo, pg, totalPages); drawFooter(doc);
    });

    curY = doc.lastAutoTable.finalY + 6;
    grandPanes += tableRows.length;
    grandQty   += jobQty;
  }

  function renderRepairGroup(group) {
    const { customerName, woNumber, tableRows } = group;
    const groupQty  = tableRows.reduce((s, r) => s + r._qty, 0);
    const gridCount = tableRows.filter(r => !!r.grids).length;

    const lineInfos = buildLineInfos(tableRows);
    if (lineInfos.length > 0) curY = drawAttentionNotice(doc, lineInfos, curY, true);

    const BH = JOB_BANNER_H;
    doc.setFillColor(...R_BG);
    doc.rect(MARGIN, curY, PAGE_W - MARGIN * 2, BH, 'F');

    doc.setDrawColor(...R_RULE); doc.setLineWidth(2);
    doc.line(MARGIN, curY, MARGIN, curY + BH);
    doc.setLineWidth(0.3);
    doc.setDrawColor(...LIGHT_GRAY);
    doc.line(MARGIN, curY + BH, PAGE_W - MARGIN, curY + BH);

    repairDisplayCounter++;
    const numStr = `R${repairDisplayCounter}`;
    doc.setFontSize(7); doc.setFont('helvetica', 'bold');
    const numW = doc.getTextWidth(numStr) + 5;
    doc.setFillColor(...R_TEXT);
    doc.roundedRect(MARGIN, curY, numW + 2, BH, 1, 1, 'F');
    doc.setTextColor(...WHITE);
    doc.text(numStr, MARGIN + (numW + 2) / 2, curY + BH - 2.2, { align: 'center' });

    const nameX = MARGIN + numW + 5;
    doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_TEXT);
    doc.text(customerName || '—', nameX, curY + BH - 2.2);

    doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(...LABEL_COLOR);
    let meta = `WO# ${woNumber || '—'}   •   ${tableRows.length} pane${tableRows.length !== 1 ? 's' : ''}   •   Qty: ${groupQty}`;
    if (gridCount > 0) meta += `   •   ${gridCount} with grids`;
    doc.text(meta, nameX + 72, curY + BH - 2.2);

    doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...R_TEXT);
    doc.text('REPAIR / WALK-IN', PAGE_W - MARGIN - 2, curY + BH - 2.2, { align: 'right' });

    curY += BH;

    const defectiveRows = tableRows.filter(r => r.defect);
    if (defectiveRows.length) {
      const DEF_RED     = [192,  57,  43];
      const DEF_BG      = [253, 232, 230];
      const DEF_GAP_TOP = 5;
      const DEF_GAP_BOT = 6;
      const dPadTop     = 5.5;
      const dPadBot     = 4.5;
      const dHeadGap    = 5.0;
      const dRowH       = 5.0;
      const dW          = PAGE_W - MARGIN * 2;
      const dH          = dPadTop + dHeadGap + defectiveRows.length * dRowH + dPadBot;

      curY += DEF_GAP_TOP;
      ensureSpace(dH + DEF_GAP_BOT);

      doc.setFillColor(...DEF_BG);
      doc.setDrawColor(...DEF_RED);
      doc.setLineWidth(0.5);
      doc.roundedRect(MARGIN, curY, dW, dH, 2, 2, 'FD');
      doc.setFillColor(...DEF_RED);
      doc.rect(MARGIN, curY, 2.4, dH, 'F');

      doc.setFontSize(7.5); doc.setFont('helvetica', 'bold');
      doc.setTextColor(...DEF_RED);
      doc.text('DEFECTIVE ON ARRIVAL   —   REPLACEMENT REQUIRED',
        MARGIN + 6, curY + dPadTop);

      doc.setFontSize(7); doc.setFont('helvetica', 'normal');
      doc.setTextColor(60, 20, 18);
      let dy = curY + dPadTop + dHeadGap;
      defectiveRows.forEach(r => {
        const reason = r.defect === 'Other'
          ? (r.defectOther && r.defectOther.trim() ? r.defectOther.trim() : 'Other defect')
          : r.defect;
        doc.text(`Item ${r.label || 'Repair'}   —   ${reason}`, MARGIN + 6, dy);
        dy += dRowH;
      });

      curY += dH + DEF_GAP_BOT;
    }

    renderTable(doc, tableRows, curY, repairColStyles,
      { fillColor: R_BG, textColor: R_TEXT },
      () => { pg++; drawStrip(doc, dateFrom, dateTo, pg, totalPages); drawFooter(doc); }
    );

    curY = doc.lastAutoTable.finalY + 6;
    grandQty   += groupQty;
    grandPanes += tableRows.length;
  }

  // Bin-packing: walk pages, slot in whatever fits in order
  while (placed.some(p => !p)) {
    const availableTop = curY;
    let placedThisPass = false;

    for (let i = 0; i < renderQueue.length; i++) {
      if (placed[i]) continue;
      const item = renderQueue[i];

      const remaining = (PAGE_H - FTR_H - 6) - curY;

      // If group is taller than ANY whole page, it must paginate via autoTable.
      // Only place it if we're already at the top of a fresh page.
      const fullPageSafe = maxSafeHeight(false);
      const tooBigForOnePage = item.height > fullPageSafe;
      const atFreshPageTop   = (pg === 1 ? curY <= HDR_H + 5 : curY <= STRIP_H + 5);

      if (item.height <= remaining || (tooBigForOnePage && atFreshPageTop)) {
        if (item.kind === 'job') renderJobGroup(item.data);
        else                     renderRepairGroup(item.data);
        placed[i] = true;
        placedThisPass = true;
      }
    }

    // Nothing fit and we still have unplaced items → new page
    if (!placedThisPass && placed.some(p => !p)) {
      newPage();
      // FORCE-PLACE guard: after a fresh page, if the first unplaced item still
      // does not satisfy the height test (its height lands between a fresh
      // page's remaining space and the tooBigForOnePage threshold), render it
      // anyway. autoTable paginates it if needed. Without this the loop spins
      // forever on that one item. (Canonical: WO#8959, leftover h=55.)
      const firstUnplaced = placed.findIndex(p => !p);
      if (firstUnplaced !== -1) {
        const it = renderQueue[firstUnplaced];
        const remNow = (PAGE_H - FTR_H - 6) - curY;
        if (it.height > remNow) {
          if (it.kind === 'job') renderJobGroup(it.data);
          else                   renderRepairGroup(it.data);
          placed[firstUnplaced] = true;
        }
      }
    }

    // Safety: if curY didn't advance and nothing was placed, force-break to avoid infinite loop
    if (!placedThisPass && curY === availableTop && placed.every(p => !p)) {
      console.error('Bin-packing: stuck, forcing new page');
      newPage();
    }
  }

  // ── Grand totals bar ──────────────────────────────────────────────────────
  const summH = 9;
  if (curY + summH + 4 > PAGE_H - FTR_H - 6) newPage();

  doc.setFillColor(...LIGHT_GRAY);
  doc.roundedRect(MARGIN, curY, 80, summH, 2, 2, 'F');
  doc.setTextColor(...BRAND_BROWN); doc.setFontSize(8); doc.setFont('helvetica', 'bold');
  doc.text(`Total Lines: ${grandPanes}   •   Jobs: ${groups.length}`, MARGIN + 4, curY + summH - 2.5);

  doc.setFillColor(...BLUE_BG);
  doc.roundedRect(MARGIN + 84, curY, 60, summH, 2, 2, 'F');
  doc.setTextColor(...BLUE_TEXT);
  doc.text(`Total Units: ${grandQty}`, MARGIN + 88, curY + summH - 2.5);

  if (repairGroups.length > 0) {
    const totalRepairItems = repairGroups.reduce((s, g) => s + g.tableRows.length, 0);
    doc.setFillColor(255, 237, 213);
    doc.roundedRect(MARGIN + 148, curY, 90, summH, 2, 2, 'F');
    doc.setTextColor(154, 52, 0);
    doc.text(
      `Repair Customers: ${repairGroups.length}   •   Items: ${totalRepairItems}`,
      MARGIN + 152, curY + summH - 2.5
    );
  }

  const cd = d => d.replace(/[\s,/]/g, '-');
  const fn = `TWP_GlassOrder_${cd(dateFrom)}_to_${cd(dateTo)}.pdf`;

  // VERIFICATION HOOK — when returnData is set, hand back the row groups so the
  // export checkpoint can compare the ACTUAL printed quantities against the
  // database. Does not save or download — the caller decides what happens next.
  // ── Stamp correct page numbers ─────────────────────────────────────────
  // The upfront countPages() is an ESTIMATE — autoTable may paginate
  // differently than predicted. Override each page's "X of Y" with the
  // truth from doc.getNumberOfPages() after all rendering is done.
  const actualTotal = doc.getNumberOfPages();
  for (let i = 1; i <= actualTotal; i++) {
    doc.setPage(i);
    // White out the previous "X of Y" text at the top-right of the strip.
    // Coords match drawStrip's text position; assumes drawPage1Header puts
    // page count in the same right-edge location (consistent header design).
    doc.setFillColor(...WHITE);
    doc.rect(PAGE_W - MARGIN - 28, 1, 28, STRIP_H - 1.5, 'F');
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...DARK);
    doc.text(`${i} of ${actualTotal}`, PAGE_W - MARGIN, STRIP_H - 2.5, { align: 'right' });
  }

  if (returnData) {
    return { blob: doc.output('blob'), groups, repairGroups };
  }

  if (returnBlob) return doc.output('blob');
  doc.save(fn);
}